/** @format */

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, StateKey, TransferState, inject, makeStateKey } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  Scene7MediaSetData,
  Scene7MediaSetItemMin,
  Scene7MediaSetResponse,
} from './interfaces/scene7-media-set-response.interface';
import { catchError, map, retry } from 'rxjs/operators';

import { Environment } from '@shared/data-access/interfaces';

interface RequestUrlParams {
  cdnName?: string;
  eCode: string;
  mediaSetSuffix: string;
  swatchColor: string;
  swatchColorPrefix: string;
}

interface HttpOptions {
  params: HttpParams;
  responseType: 'text';
}

@Injectable({
  providedIn: 'root',
})
export class Scene7Dao {
  readonly MEDIA_SET_PARSE_ERROR_PREFIX = 'SCENE7_RESPONSE_PARSE_ERROR:';
  readonly MEDIA_SET_STATE_KEY_PREFIX = 'media-set';

  private readonly environment = inject(Environment);
  private readonly http = inject(HttpClient);
  private readonly state = inject(TransferState);

  private getStateByKey(
    eCode: string,
    swatchColor: string,
    namesSet: boolean,
    cdnName?: string,
  ): { STATE_KEY: StateKey<Scene7MediaSetData>; stateData: Scene7MediaSetData | null } {
    const stateKeyCdnVal = cdnName ? `-${cdnName}` : '';
    // Get unique key; include cdnName so different CDN overrides don't collide
    const STATE_KEY = makeStateKey<Scene7MediaSetData>(
      `${this.MEDIA_SET_STATE_KEY_PREFIX}-${eCode}-${swatchColor}${stateKeyCdnVal}${namesSet ? '-namesSet' : ''}`,
    );

    const stateData = this.state.get<Scene7MediaSetData | null>(STATE_KEY, null);

    // Attempt to get the state by key
    return { STATE_KEY, stateData };
  }

  private createHttpOptions(): HttpOptions {
    // Query String Parameters
    const httpParams = new HttpParams()
      .set('req', 'set,json,UTF-8')
      .set('labelkey', 'label')
      .set('handler', 'customScene7Handler');

    // HTTP Options
    return {
      params: httpParams,
      responseType: 'text' as const,
    };
  }

  private requestError(error: SyntaxError): Error {
    return new Error(
      `${this.MEDIA_SET_PARSE_ERROR_PREFIX}${JSON.stringify(error, [
        'message',
        'arguments',
        'type',
        'name',
      ])}`,
    );
  }

  private getRequestUrl(params: RequestUrlParams): string {
    const baseConfig = this.environment.thirdParty.scene7?.baseConfig;
    if (!baseConfig) {
      throw new Error('Scene7 base configuration is missing');
    }
    const { baseUrl, imageDirectory } = baseConfig;
    const { cdnName, eCode, swatchColorPrefix, swatchColor, mediaSetSuffix } = params;

    return `${baseUrl}${imageDirectory}${cdnName}${eCode}${swatchColorPrefix}${swatchColor}${mediaSetSuffix}`;
  }

  private createMediaSetResult(response: string): Scene7MediaSetResponse {
    // List of unwanted text patterns to be removed
    const unwantedPatterns = [
      '/*jsonp*/',
      's7jsonError(',
      'customScene7Handler(',
      ', "");',
      ',"");',
    ];

    // Remove unwanted text patterns and trim the response
    let formattedResponse = response;
    for (const pattern of unwantedPatterns) {
      formattedResponse = formattedResponse.replace(pattern, '');
    }

    formattedResponse = formattedResponse.trim();

    // Parse the JSON string and return the parsed media set
    return JSON.parse(formattedResponse).set;
  }

  private getNamesAndTypesList(imagesSet: Scene7MediaSetResponse): Scene7MediaSetItemMin[] {
    const items = Array.isArray(imagesSet.item) ? imagesSet.item : [imagesSet.item];

    return items.map((listItem) => {
      const imagePath = listItem.i?.n ?? listItem.s?.n;

      if (!imagePath) {
        throw new Error('Scene7 media item is missing an image path');
      }

      const imageName = imagePath.split('/').pop();

      if (!imageName) {
        throw new Error('Scene7 media item has an invalid image path');
      }

      const isImage = imagesSet.type === 'img_set' || !listItem.type;
      const mediaSet: Scene7MediaSetItemMin = { imageName, isImage };

      if (listItem.type === 'video_set') {
        if (!listItem.set) {
          throw new Error('Scene7 video set is missing its set data');
        }

        const videoItems = Array.isArray(listItem.set.item)
          ? listItem.set.item
          : [listItem.set.item];

        mediaSet.videoSrcs = videoItems.map((setItem) => {
          const videoPath = setItem.i?.n;
          if (!videoPath) {
            throw new Error('Scene7 video set item is missing an image path');
          }
          return videoPath;
        });
      } else if (listItem.type === 'video') {
        mediaSet.videoSrcs = [imageName];
      }

      return mediaSet;
    });
  }

  private getImageDataResponse(
    params: RequestUrlParams,
    retries: number,
  ): Observable<Scene7MediaSetResponse> {
    const { eCode, swatchColor, swatchColorPrefix, mediaSetSuffix } = params;
    const imageCdn = this.environment.thirdParty.scene7?.baseConfig?.cdn;
    const httpOptions = this.createHttpOptions();
    const url = this.getRequestUrl({
      cdnName: params.cdnName ?? imageCdn,
      eCode,
      swatchColorPrefix,
      swatchColor,
      mediaSetSuffix,
    });
    // Make the HTTP Request
    return this.http.get(url, httpOptions).pipe(
      map((response) => {
        return this.createMediaSetResult(response);
      }),
      retry(retries),
      catchError((error) => {
        throw this.requestError(error);
      }),
    );
  }

  private getImageDataWithNames(
    params: RequestUrlParams,
    retries: number,
  ): Observable<Scene7MediaSetItemMin[]> {
    const { eCode, swatchColor, cdnName } = params;
    const { STATE_KEY, stateData } = this.getStateByKey(eCode, swatchColor, true, cdnName);

    // Check to see if state exists
    if (stateData && Array.isArray(stateData)) {
      return of(stateData);
    }

    return this.getImageDataResponse(params, retries).pipe(
      map((imagesSet) => {
        const mediaSetNames = this.getNamesAndTypesList(imagesSet);
        this.state.set(STATE_KEY, mediaSetNames);
        return mediaSetNames;
      }),
    );
  }

  private getImageData(
    params: RequestUrlParams,
    retries: number,
  ): Observable<Scene7MediaSetResponse> {
    const { eCode, swatchColor, cdnName } = params;
    const { STATE_KEY, stateData } = this.getStateByKey(eCode, swatchColor, false, cdnName);

    // Check to see if state exists
    if (stateData && !Array.isArray(stateData)) {
      return of(stateData);
    }

    return this.getImageDataResponse(params, retries).pipe(
      map((imagesSet) => {
        this.state.set(STATE_KEY, imagesSet);
        return imagesSet;
      }),
    );
  }

  /**
   * @description
   * Gets an image set from Scene7 for a given product and supplied arguments
   */
  getProductImageSet$(
    eCode: string,
    swatchColor: string,
    swatchColorPrefix: string,
    mediaSetSuffix: string,
    retries = 0,
    cdn?: string,
  ): Observable<Scene7MediaSetResponse> {
    return this.getImageData(
      { eCode, swatchColor, swatchColorPrefix, mediaSetSuffix, cdnName: cdn },
      retries,
    );
  }

  /**
   * @description
   * Gets image set names from Scene7 for a given product and supplied arguments
   */
  getProductImageSetNames$(
    eCode: string,
    swatchColor: string,
    swatchColorPrefix: string,
    mediaSetSuffix: string,
    retries = 0,
    cdn?: string,
  ): Observable<Scene7MediaSetItemMin[]> {
    return this.getImageDataWithNames(
      { eCode, swatchColor, swatchColorPrefix, mediaSetSuffix, cdnName: cdn },
      retries,
    );
  }
}
